import axios from "axios";

// Always call the API on the same origin the page was loaded from.
// This eliminates cross-origin (CORS) requests in every environment:
//   • Preview at preview.emergentagent.com → /api on preview.emergentagent.com
//   • Production at ascinterior.com → /api on ascinterior.com
//   • Production at asc-interior-experts.emergent.host → /api on emergent.host
// `REACT_APP_BACKEND_URL` is only used as a server-side / build-time fallback.
const BACKEND_URL =
    typeof window !== "undefined" && window.location && window.location.origin
        ? window.location.origin
        : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// Public
export const fetchProjects = () => api.get("/projects").then((r) => r.data.projects);
export const fetchProject = (slug) => api.get(`/projects/${slug}`).then((r) => r.data);
export const fetchServices = () => api.get("/services").then((r) => r.data.services);
export const fetchTestimonials = () => api.get("/testimonials").then((r) => r.data.testimonials);
export const fetchClients = () => api.get("/clients").then((r) => r.data.clients);
export const fetchAwards = () => api.get("/awards").then((r) => r.data);
export const fetchJournal = () => api.get("/journal").then((r) => r.data.posts);
export const fetchJournalPost = (slug) => api.get(`/journal/${slug}`).then((r) => r.data);
export const fetchStudio = () => api.get("/studio").then((r) => r.data);
// Studio Life (articles) — public
export const fetchArticles = () => api.get("/articles").then((r) => r.data.articles);
export const fetchArticle = (slug) => api.get(`/articles/${slug}`).then((r) => r.data);
// Services page — public
export const fetchServicesPage = () => api.get("/services-page").then((r) => r.data);
export const submitInquiry = (payload) => api.post("/contact", payload).then((r) => r.data);

// Admin (httpOnly cookie session)
export const adminLogin = (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data);
export const adminLogout = () => api.post("/auth/logout").then((r) => r.data);
export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const fetchInquiries = () => api.get("/inquiries").then((r) => r.data);

// Admin — Projects CMS
export const adminListProjects = () => api.get("/admin/projects").then((r) => r.data.projects);
export const adminGetProject = (slug) => api.get(`/projects/${slug}`).then((r) => r.data);
export const adminCreateProject = (payload) => api.post("/admin/projects", payload).then((r) => r.data);
export const adminUpdateProject = (slug, payload) =>
    api.put(`/admin/projects/${slug}`, payload).then((r) => r.data);
export const adminDeleteProject = (slug) =>
    api.delete(`/admin/projects/${slug}`).then((r) => r.data);
export const adminReorderProjects = (slugs) =>
    api.post("/admin/projects/reorder", { slugs }).then((r) => r.data);

// Admin — Media Library
export const adminListMedia = (params = {}) =>
    api.get("/admin/media", { params }).then((r) => r.data.media);
export const adminUploadMedia = (files, onProgress) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
    }).then((r) => r.data);
};
export const adminImportMediaUrl = (url, filename) =>
    api.post("/admin/media/import-url", { url, filename }).then((r) => r.data);
export const adminDeleteMedia = (id) =>
    api.delete(`/admin/media/${id}`).then((r) => r.data);

// Public + admin — Site Settings
export const fetchSettings = () => api.get("/settings").then((r) => r.data);
export const adminUpdateSettings = (payload) =>
    api.put("/admin/settings", payload).then((r) => r.data);

// Admin — Services Page
export const adminUpdateServicesPage = (payload) =>
    api.put("/admin/services-page", payload).then((r) => r.data);

// Admin — Studio Life (articles)
export const adminListArticles = () => api.get("/admin/articles").then((r) => r.data.articles);
export const adminGetArticle = (slug) => api.get(`/admin/articles/${slug}`).then((r) => r.data);
export const adminCreateArticle = (payload) => api.post("/admin/articles", payload).then((r) => r.data);
export const adminUpdateArticle = (slug, payload) =>
    api.put(`/admin/articles/${slug}`, payload).then((r) => r.data);
export const adminDeleteArticle = (slug) =>
    api.delete(`/admin/articles/${slug}`).then((r) => r.data);

// Admin — Inquiries CRM
export const adminListInquiries = (params = {}) =>
    api.get("/admin/inquiries", { params }).then((r) => r.data);
export const adminGetInquiry = (id) => api.get(`/admin/inquiries/${id}`).then((r) => r.data);
export const adminUpdateInquiry = (id, patch) =>
    api.patch(`/admin/inquiries/${id}`, patch).then((r) => r.data);
export const adminDeleteInquiry = (id) =>
    api.delete(`/admin/inquiries/${id}`).then((r) => r.data);
export const adminExportInquiries = (ids) =>
    api.post("/admin/inquiries/export", { ids }, { responseType: "blob" }).then((r) => r.data);

// Admin — Clients CMS
export const adminListClients = () => api.get("/admin/clients").then((r) => r.data.clients);
export const adminCreateClient = (payload) => api.post("/admin/clients", payload).then((r) => r.data);
export const adminUpdateClient = (id, payload) =>
    api.put(`/admin/clients/${id}`, payload).then((r) => r.data);
export const adminDeleteClient = (id) =>
    api.delete(`/admin/clients/${id}`).then((r) => r.data);
export const adminReorderClients = (ids) =>
    api.post("/admin/clients/reorder", { ids }).then((r) => r.data.clients);

// Admin — Email settings
export const adminGetEmailSettings = () => api.get("/admin/email-settings").then((r) => r.data);
export const adminUpdateEmailSettings = (payload) =>
    api.put("/admin/email-settings", payload).then((r) => r.data);
export const adminTestEmail = () =>
    api.post("/admin/email-settings/test").then((r) => r.data);

// Admin — Dashboard
export const adminDashboard = () => api.get("/admin/dashboard").then((r) => r.data);

// Admin — Recognition (Awards & Honours) CMS
export const adminGetRecognition = () => api.get("/admin/recognition").then((r) => r.data);
export const adminUpdateRecognition = (payload) =>
    api.put("/admin/recognition", payload).then((r) => r.data);

// Helper: turn a media URL (which may be relative like "/api/media/xxx/raw") into a full URL
// that <img> can render even from a different origin. For external URLs (http(s)://...) it's a no-op.
export const mediaUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//.test(url)) return url;
    return `${BACKEND_URL}${url}`;
};
